/**
 * ANALYTICS FOUNDATION — a safe, provider-agnostic event layer.
 *
 * No tracking IDs are hardcoded and nothing is sent anywhere until a provider
 * script is added to public/index.html. Until then every track() call is a
 * harmless no-op (or a console line in debug mode).
 *
 * ✏️ HOW TO CONNECT A PROVIDER (for Julio & Nick — pick ONE):
 *   Google Analytics 4: paste the GA4 snippet from analytics.google.com into
 *     public/index.html <head>. track() auto-detects `gtag` and starts sending.
 *   Plausible (privacy-friendly, no cookie banner needed): paste their script
 *     tag into public/index.html. track() auto-detects `plausible`.
 *
 * 🐛 DEBUG MODE: set REACT_APP_ANALYTICS_DEBUG=true in a .env file to see
 *     events in the browser console while testing.
 */

export type AnalyticsEvent =
  | "page_viewed"
  | "pickup_cta_clicked"
  | "pickup_form_started"
  | "pickup_form_completed"
  | "pickup_form_error"
  | "zip_submitted"
  | "item_searched"
  | "item_category_selected"
  | "audience_selected"
  | "business_quote_submitted"
  | "newsletter_submitted"
  | "faq_opened"
  | "social_clicked"
  | "email_clicked"
  | "form_error";

export function track(event: AnalyticsEvent, props?: Record<string, string | number | boolean>): void {
  try {
    if (process.env.REACT_APP_ANALYTICS_DEBUG === "true") {
      // eslint-disable-next-line no-console
      console.info("[analytics]", event, props || {});
    }
    const w = window as any;
    if (typeof w.gtag === "function") {
      w.gtag("event", event, props || {});
    } else if (typeof w.plausible === "function") {
      w.plausible(event, { props: props || {} });
    }
    // No provider on the page → intentional no-op. Never throws, never blocks UI.
  } catch {
    /* analytics must never break the site */
  }
}
